import { TabSection } from './TabSection'
import { Toggle } from '../Toggle'
import type { ChannelConfigUpdate, NotificationType, OpenNotificationTemplate, ReviewReplyTemplate } from '../../types'
import styles from './ReviewUpdatesTab.module.css'

const ACTIVITY_TYPES: NotificationType[] = ['REVIEW_DECISION', 'REVIEW_COMMENT', 'MERGE_CLOSE']

const REACTION_EMOJI: Partial<Record<NotificationType, string>> = {
  REVIEW_DECISION: '/emoji-approved.png',
  REVIEW_COMMENT:  '/emoji-comment.png',
  MERGE_CLOSE:     '/emoji-merged.png',
}

const ACTIVITY_META: Record<NotificationType, { label: string; hint: string }> = {
  REVIEW_REQUEST:  { label: 'New PR',          hint: '' },
  REVIEW_DECISION: { label: 'Review decision',  hint: 'Someone approved or requested changes on a PR' },
  REVIEW_COMMENT:  { label: 'Review comment',   hint: 'Someone left comments with no approve/reject decision — opt-in' },
  MERGE_CLOSE:     { label: 'Merge / close',    hint: 'PR was merged or closed' },
}

const OPEN_TEMPLATES: { value: OpenNotificationTemplate; name: string; desc: string; image: string }[] = [
  { value: 'LEAN',     name: 'Lean',     desc: 'Compact — title, author, and repo',          image: '/template-lean.png' },
  { value: 'STANDARD', name: 'Standard', desc: 'Full context — overview, diff stats, and CTA', image: '/template-standard.png' },
]

const REPLY_TEMPLATES: { value: ReviewReplyTemplate; name: string; desc: string; images: string[] }[] = [
  { value: 'LEAN',     name: 'Lean',     desc: 'Signal only — minimal copy', images: ['/reply-lean-approved.png',     '/reply-lean-req-changes.png'] },
  { value: 'STANDARD', name: 'Standard', desc: 'Friendly — default',         images: ['/reply-standard-approved.png', '/reply-standard-req-changes.png'] },
]

// Toggle a type in/out of a concrete array. Always returns a plain NotificationType[].
// ['DEFAULTS'] is only sent explicitly when a reset action is triggered, never auto-collapsed.
function toggleInSet(
  current: NotificationType[],
  type: NotificationType,
  on: boolean,
): NotificationType[] {
  return on
    ? [...new Set([...current, type])]
    : current.filter(t => t !== type)
}

interface Props {
  draft: {
    enabledNotifications: NotificationType[]
    reactionNotifications: NotificationType[]
    replyNotifications: NotificationType[]
    openNotificationTemplate: OpenNotificationTemplate | undefined
    reviewReplyTemplate: ReviewReplyTemplate | undefined
    mergeStatsEnabled: boolean | undefined
  }
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
}

export function ReviewUpdatesTab({ draft, onChange }: Props) {
  const openTemplate  = draft.openNotificationTemplate ?? 'STANDARD'
  const replyTemplate = draft.reviewReplyTemplate ?? 'STANDARD'

  const newPrOn      = draft.enabledNotifications.includes('REVIEW_REQUEST')
  const anyActivityOn = ACTIVITY_TYPES.some(t => draft.enabledNotifications.includes(t))

  const selectedOpenTemplate  = OPEN_TEMPLATES.find(t => t.value === openTemplate)  ?? OPEN_TEMPLATES[1]
  const selectedReplyTemplate = REPLY_TEMPLATES.find(t => t.value === replyTemplate) ?? REPLY_TEMPLATES[1]

  function toggleOn(type: NotificationType, on: boolean) {
    const next = toggleInSet(draft.enabledNotifications, type, on)
    const patch: Partial<ChannelConfigUpdate> = { enabledNotifications: next }
    // turning a row off → remove from reaction/reply sub-columns too
    if (!on) {
      patch.reactionNotifications = draft.reactionNotifications.filter(t => t !== type)
      patch.replyNotifications    = draft.replyNotifications.filter(t => t !== type)
    }
    onChange(patch)
  }

  return (
    <TabSection
      accent="teal"
      icon="notifications"
      title="Review Updates"
      subtitle="Choose which PR events are posted and how they appear"
    >
      {/* ── New PR ───────────────────────────────────────────── */}
      <div className={styles.notifList}>
        <div className={styles.notifRow}>
          <div className={styles.notifMain}>
            <div className={styles.notifInfo}>
              <div className={styles.rowLabel}>New PR</div>
              <div className={styles.rowHint}>
                Get notified when a PR is opened or a review is requested from a team or user you're subscribed to
              </div>
            </div>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={newPrOn}
                onChange={e => toggleOn('REVIEW_REQUEST', e.target.checked)}
              />
              <div className={styles.track} />
              <div className={styles.thumb} />
            </label>
          </div>

          {newPrOn && (
            <div className={styles.inlineSection}>
              <div className={styles.inlineSectionLabel}>Notification style</div>
              <div className={styles.templateOptions}>
                {OPEN_TEMPLATES.map(t => (
                  <div
                    key={t.value}
                    className={`${styles.templateOption} ${openTemplate === t.value ? styles.templateOptionActive : ''}`}
                    onClick={() => onChange({ openNotificationTemplate: t.value })}
                  >
                    <div className={styles.templateName}>{t.name}</div>
                    <div className={styles.templateDesc}>{t.desc}</div>
                  </div>
                ))}
              </div>
              <div className={styles.templatePreviewImg}>
                <img src={selectedOpenTemplate.image} alt={`${selectedOpenTemplate.name} template example`} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── PR activity ──────────────────────────────────────── */}
      <div className={styles.sectionDivider}>PR activity</div>
      <div className={styles.notifList}>
        {ACTIVITY_TYPES.map(type => {
          const { label, hint } = ACTIVITY_META[type]
          const isOn      = draft.enabledNotifications.includes(type)
          const emojiSrc  = REACTION_EMOJI[type]
          const reactionOn = isOn && draft.reactionNotifications.includes(type)
          const replyOn    = isOn && draft.replyNotifications.includes(type)
          return (
            <div key={type} className={styles.notifRow}>
              <div className={styles.notifMain}>
                <div className={styles.notifInfo}>
                  <div className={styles.rowLabel}>{label}</div>
                  <div className={styles.rowHint}>{hint}</div>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={e => toggleOn(type, e.target.checked)}
                  />
                  <div className={styles.track} />
                  <div className={styles.thumb} />
                </label>
              </div>

              {isOn && (
                <div className={styles.subOptions}>
                  <div
                    className={`${styles.subOption} ${reactionOn ? styles.subOptionOn : ''}`}
                    onClick={() =>
                      onChange({
                        reactionNotifications: toggleInSet(draft.reactionNotifications, type, !reactionOn),
                      })
                    }
                  >
                    {emojiSrc && <img src={emojiSrc} alt="" className={styles.emojiIcon} />}
                    <span className={styles.subLabel}>React</span>
                  </div>
                  <div
                    className={`${styles.subOption} ${replyOn ? styles.subOptionOn : ''}`}
                    onClick={() =>
                      onChange({
                        replyNotifications: toggleInSet(draft.replyNotifications, type, !replyOn),
                      })
                    }
                  >
                    <span className={styles.subLabel}>Thread update</span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* ── Update style (conditional) ───────────────────────── */}
      {draft.enabledNotifications.includes('MERGE_CLOSE') && (
        <div style={{ marginBottom: 24 }}>
          <Toggle
            label="Merge stats"
            hint="Include review timing stats in the merge notification — first review in, time in review, PR open for, and review rounds"
            checked={draft.mergeStatsEnabled ?? false}
            onChange={v => onChange({ mergeStatsEnabled: v })}
            noBorder
          />
        </div>
      )}

      {anyActivityOn && (
        <div className={styles.templateSection}>
          <div className={styles.sectionLabel}>Review update style</div>
          <div className={styles.sectionSubLabel}>
            Review updates are posted as thread updates on the original new PR message.
          </div>
          <div className={styles.templateOptions}>
            {REPLY_TEMPLATES.map(t => (
              <div
                key={t.value}
                className={`${styles.templateOption} ${replyTemplate === t.value ? styles.templateOptionActive : ''}`}
                onClick={() => onChange({ reviewReplyTemplate: t.value })}
              >
                <div className={styles.templateName}>{t.name}</div>
                <div className={styles.templateDesc}>{t.desc}</div>
              </div>
            ))}
          </div>
          <div className={styles.templatePreviewImg}>
            {selectedReplyTemplate.images.map((src, i) => (
              <img key={i} src={src} alt={`${selectedReplyTemplate.name} example ${i + 1}`} />
            ))}
          </div>
        </div>
      )}

    </TabSection>
  )
}
